import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  output,
  OutputEmitterRef,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import iro from '@jaames/iro';
import { MatteToggleButtonComponent } from '../matte-toggle-button/matte-toggle-button.component';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { CookieService } from 'ngx-cookie-service';
const COOKIE_OPTIONS = {
  path: '/',
};
@Component({
  selector: 'color-picker',
  imports: [CommonModule, MatteToggleButtonComponent, MatTabsModule],
  templateUrl: './color-picker.component.html',
  styleUrl: './color-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mainDom') mainDom: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('boxColorPicker') boxColorPicker: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('slidersColorPicker') slidersColorPicker: ElementRef<HTMLDivElement> | undefined;

  public changeColor: OutputEmitterRef<string> = output<string>();
  public changematte: OutputEmitterRef<boolean> = output<boolean>();

  public isChangeColor: boolean = true;

  public selectedColorListTabIndex: number = 0;

  public layoutDirection: 'horizontal' | 'vertical' = 'horizontal';

  private colorChangeSubject = new Subject<string>();

  public red: string | undefined;
  public green: string | undefined;
  public blue: string | undefined;
  public color: string | undefined;

  public defaultColors: string[] = [
    '#FFFFFF',
    '#000000',
    '#BEBEBE',
    '#C0C0C0',
    '#003366',
    '#C00000',
    '#4B4B4B',
    '#1D3557',
    '#8B4513',
    '#013220',
  ];

  public beforeElWidth: number = 0;
  public width: number = 150;

  private boxColorPickerIro: iro.ColorPicker | undefined;

  private resizeObserver: ResizeObserver | undefined;

  constructor(
    private cookie: CookieService,
    private cd: ChangeDetectorRef,
  ) {}

  get historyColors(): string[] {
    return (
      this.cookie
        .get('historyColors')
        ?.split(',')
        .filter((color: string) => color) || []
    );
  }

  set historyColors(colors: string[]) {
    this.cookie.set('historyColors', colors.join(','), COOKIE_OPTIONS);
  }

  //--------------------------------------------------------------------------------
  // @ Lifecycle hooks
  //--------------------------------------------------------------------------------

  ngAfterViewInit(): void {
    this.initColorPicker();
    this.observeResize();
    this.colorChangeSubject.pipe(debounceTime(500)).subscribe((color) => {
      this.addColorToHistory(color);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.colorChangeSubject.complete();
    this.colorChangeSubject.unsubscribe();
  }

  //--------------------------------------------------------------------------------
  // @ Public methods
  //--------------------------------------------------------------------------------

  public onClicColor(color: string): void {
    if (!this.boxColorPicker) return;

    this.boxColorPickerIro?.color.set(color);
  }

  public onChangeMattr(boolean: boolean): void {
    this.changematte.emit(boolean);
  }

  //--------------------------------------------------------------------------------
  // @ Private methods
  //--------------------------------------------------------------------------------

  private initColorPicker(): void {
    this.initBoxColorPicker();
  }
  private initBoxColorPicker(): void {
    const mainLayoutOptions: any = [
      {
        component: iro.ui.Box,
      },
      {
        component: iro.ui.Slider,
        options: {
          sliderType: 'hue',
          sliderSize: 10,
        },
      },
    ];
    if (!this.boxColorPicker?.nativeElement) return;
    this.width = (this.mainDom?.nativeElement.clientWidth || 150) - (this.width > 300 ? 50 : 30);
    this.boxColorPickerIro = iro.ColorPicker(this.boxColorPicker.nativeElement, {
      width: this.width > 300 ? this.width / 2.25 : this.width,
      height: 50,
      color: this.color || '#ffffff',
      borderWidth: 0,
      borderColor: '#fff',
      layoutDirection: 'horizontal',
      margin: 10,
      layout: mainLayoutOptions,
    });
    this.getBoxColorPickerColorEvent();
  }

  private observeResize(): void {
    if (!this.mainDom) return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.beforeElWidth == this.mainDom?.nativeElement.clientWidth) return;
      const newWidth =
        (this.mainDom?.nativeElement.clientWidth || 150) -
        (this.mainDom?.nativeElement.clientWidth || 0 > 300 ? 50 : 30);
      const newDirection: 'horizontal' | 'vertical' = newWidth > 300 ? 'vertical' : 'horizontal';

      if (newDirection !== this.layoutDirection) {
        this.layoutDirection = newDirection;
        this.width = newWidth;
        this.recreateBoxColorPicker();
      } else {
        this.width = newWidth;
        this.boxColorPickerIro?.resize(this.width > 300 ? this.width / 2.25 : this.width);
      }
      if (this.mainDom?.nativeElement.clientWidth)
        this.beforeElWidth = this.mainDom?.nativeElement.clientWidth;
      this.cd.markForCheck();
    });

    this.resizeObserver.observe(this.mainDom.nativeElement);
  }

  private recreateBoxColorPicker(): void {
    if (!this.boxColorPicker?.nativeElement) return;

    if (this.boxColorPickerIro) {
      this.boxColorPickerIro = undefined;
      this.boxColorPicker.nativeElement.innerHTML = '';
    }

    const layoutOptions: any = [
      { component: iro.ui.Box },
      {
        component: iro.ui.Slider,
        options: {
          sliderType: 'hue',
          sliderSize: 10,
        },
      },
    ];

    const width = this.width > 300 ? this.width / 2.25 : this.width;

    this.boxColorPickerIro = iro.ColorPicker(this.boxColorPicker.nativeElement, {
      width,
      height: 50,
      color: this.color || '#ffffff',
      borderWidth: 0,
      borderColor: '#fff',
      layoutDirection: this.layoutDirection,
      margin: 10,
      layout: layoutOptions,
    });

    this.getBoxColorPickerColorEvent();
  }

  private getBoxColorPickerColorEvent(): void {
    if (!this.boxColorPickerIro) return;
    this.boxColorPickerIro.on(['color:change'], (color: iro.Color) => {
      this.color = color.hexString;
      this.red = color.red.toString();
      this.green = color.green.toString();
      this.blue = color.blue.toString();
      if (this.isChangeColor) {
        this.changeColor.emit(this.color);
        this.colorChangeSubject.next(this.color);
      }
      this.isChangeColor = true;
      this.cd.markForCheck();
    });
  }

  private addColorToHistory(color: string): void {
    if (!color) return;
    const history = this.historyColors;
    if (this.historyColors.includes(color)) return;

    if (this.historyColors.length > 20) history.pop();

    this.historyColors = [color, ...history];

    this.cd.markForCheck();
  }
}
